import { ArrowRightOutlined, CheckOutlined } from "@ant-design/icons"
import { Button, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { brand, neutral } from "../../../../components/widgets/tokens.ts"

const { Text } = Typography

export type WizardStep = 1 | 2 | 3 | 4

export const WIZARD_STEPS: {
  n: WizardStep
  labelKey: string
  optional?: boolean
}[] = [
  { n: 1, labelKey: "reports.wizard.steps.general" },
  { n: 2, labelKey: "reports.wizard.steps.build" },
  { n: 3, labelKey: "reports.wizard.steps.filters", optional: true },
  { n: 4, labelKey: "reports.wizard.steps.share" }
]

interface ReportWizardStepperProps {
  current: WizardStep
  completed: Set<WizardStep>
  /** Every step reads as done: the report was created. */
  done?: boolean
  canGoTo?: (step: WizardStep) => boolean
  onGoTo?: (step: WizardStep) => void
}

/**
 * All four steps, always: a creator can see how much is left. A step already
 * reached can be returned to; the ones ahead are shown but not offered.
 */
export const ReportWizardStepper = ({
  current,
  completed,
  done = false,
  canGoTo = () => false,
  onGoTo
}: ReportWizardStepperProps) => {
  const { t } = useTranslation()
  return (
    <ol
      aria-label={t("reports.wizard.stepperLabel")}
      className="m-0 flex list-none items-center gap-2 overflow-x-auto p-0"
    >
      {WIZARD_STEPS.map((step, index) => {
        const isCurrent = !done && step.n === current
        const isDone = done || (completed.has(step.n) && !isCurrent)
        const reachable = !done && !isCurrent && canGoTo(step.n)
        const label = t(step.labelKey)
        return (
          <React.Fragment key={step.n}>
            {index > 0 && (
              <li
                aria-hidden
                className="h-px min-w-6 flex-1"
                style={{
                  background: isDone || isCurrent ? brand.teal : neutral.border
                }}
              />
            )}
            <li
              aria-current={isCurrent ? "step" : undefined}
              className="shrink-0"
            >
              <button
                type="button"
                disabled={!reachable}
                onClick={() => onGoTo?.(step.n)}
                aria-label={t("reports.wizard.stepOf", {
                  number: step.n,
                  count: WIZARD_STEPS.length,
                  name: label
                })}
                className={`flex items-center gap-2 border-0 bg-transparent p-0 text-left ${
                  reachable ? "cursor-pointer" : "cursor-default"
                }`}
              >
                <span
                  aria-hidden
                  className="flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
                  style={
                    isCurrent || isDone
                      ? { background: brand.teal, color: "#fff" }
                      : {
                          background: "#fff",
                          color: neutral.muted,
                          border: `1px solid ${neutral.border}`
                        }
                  }
                >
                  {isDone ? <CheckOutlined /> : step.n}
                </span>
                <span className="flex flex-col leading-tight">
                  <Text
                    className="whitespace-nowrap"
                    style={{
                      fontWeight: isCurrent ? 600 : 500,
                      color: isCurrent ? neutral.ink : neutral.muted
                    }}
                  >
                    {label}
                  </Text>
                  {step.optional && (
                    <Text type="secondary" className="text-xs">
                      {t("reports.wizard.optionalStep")}
                    </Text>
                  )}
                </span>
              </button>
            </li>
          </React.Fragment>
        )
      })}
    </ol>
  )
}

interface ReportWizardFooterProps {
  current: WizardStep
  nextLabel: string
  busy?: boolean
  onCancel: () => void
  onBack: () => void
  onNext: () => void
}

/** The same bar on every step, pinned under the step's own scroll area. */
export const ReportWizardFooter = ({
  current,
  nextLabel,
  busy,
  onCancel,
  onBack,
  onNext
}: ReportWizardFooterProps) => {
  const { t } = useTranslation()
  return (
    <div
      className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-0 border-t border-solid bg-white px-6 py-3 text-left"
      style={{ borderColor: neutral.border }}
    >
      <div className="justify-self-start">
        <Button size="large" onClick={onCancel} disabled={busy}>
          {t("reports.cancel")}
        </Button>
      </div>
      <Text type="secondary" className="whitespace-nowrap">
        {t("reports.wizard.stepCounter", {
          number: current,
          count: WIZARD_STEPS.length
        })}
      </Text>
      <div className="flex justify-end gap-2">
        {current > 1 && (
          <Button size="large" onClick={onBack} disabled={busy}>
            {t("reports.wizard.back")}
          </Button>
        )}
        <Button
          size="large"
          type="primary"
          loading={busy}
          onClick={onNext}
          icon={current < 4 ? <ArrowRightOutlined aria-hidden /> : undefined}
          iconPosition="end"
        >
          {nextLabel}
        </Button>
      </div>
    </div>
  )
}
